import TitleBar from '../../../components/TitleBar';
import { Button, FetchError } from '../../../components';
import { useLazyQuery, useQuery } from '@apollo/client';
import { FETCH_LOCATIONS } from '../../dataInputs/queries';
import { LocationsReturned } from '../../dataInputs/locations/Locations';
import React from 'react';
import { Footer } from '../../../components/common/Footer';

import { useNavigate } from 'react-router-dom';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import { ColumnsType } from 'antd/es/table';
import FinancialData from '../../../components/common/FinancialData';
import { REFRESH_LOCATIONS } from '../../dataInputs/queries/locations';
import QueryErrorBoundary from '../../../components/error-handling';
import { message } from 'antd';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import TitleUpdater from '../../../components/common/TitleUpdater';

export function PALocationSelection() {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<LocationsReturned>(FETCH_LOCATIONS);

  const locationsData = data?.findAllLocations || [];

  const { selectedTags, isLocation } = useUserContext();

  const filteredLocations = getFilteredTags({
    data: locationsData,
    selectedTags,
    condition: isLocation,
    lookUpKey: 'uuid',
  });

  const [refreshLocations, { loading: refreshedLoading }] =
    useLazyQuery(REFRESH_LOCATIONS);

  const columns: ColumnsType<any> = [
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      align: 'left',
      width: 200,
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationName, b.locationName),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Annual Revenue Supported',
      dataIndex: 'annualRevenueSupported',
      key: 'annualRevenueSupported',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.annualRevenueSupported,
          b.annualRevenueSupported,
        ),
      render: (text: number) => (
        <span className="custom-table--column custom-table--column-revenue">
          {<FinancialData text={text} />}
        </span>
      ),
    },
    {
      title: 'Process Areas',
      dataIndex: 'processAreasCount',
      key: 'processAreasCount',
      align: 'right',
      width: 100,

      sorter: (a, b) =>
        tableSortingMethods.number(a.processAreasCount, b.processAreasCount),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Processes',
      dataIndex: 'processesCount',
      key: 'processesCount',
      align: 'right',
      width: 100,

      sorter: (a, b) =>
        tableSortingMethods.number(a.processesCount, b.processesCount),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Process Area Mitigation Average',
      dataIndex: 'processAreasMitigationAverage',
      key: 'processAreasMitigationAverage',
      align: 'right',
      width: 100,

      sorter: (a, b) =>
        tableSortingMethods.number(
          a.processAreasMitigationAverage,
          b.processAreasMitigationAverage,
        ),
      render: (text: number) => (
        <span className="custom-table--column">
          {(text || 0).toFixed(0) + '%'}
        </span>
      ),
    },
  ];

  const extendActions = (record: any) => {
    return (
      <Button
        type="primary-rounded"
        className="text-sm font-semibold min-w-[140px]"
        onClick={() =>
          navigate(`/mitigation/${record.uuid}?location=${record.locationName}`)
        }
      >
        Quantify Mitigation
      </Button>
    );
  };

  const handleRefresh = async () => {
    try {
      const response = await refreshLocations();

      if (response.data.rollUpProcessesAndProcessAreaMitigationPercentages) {
        message.success('Locations refreshed successfully');
      }
    } catch (error) {
      message.error('Failed to refresh locations');
    }
  };

  const baseColumns: ExportColumn[] = [
    {
      key: 'locationName',
      header: 'Location',
      type: 'text',
      width: 45,
    },
    {
      key: 'annualRevenueSupported',
      header: 'Annual Revenue Supported',
      type: 'money',
      width: 45,
    },
    {
      key: 'processAreasCount',
      header: 'Process Areas',
      type: 'number',
      width: 45,
    },
    {
      key: 'processesCount',
      header: 'Processes',
      type: 'number',
      width: 45,
    },
    {
      key: 'processAreasMitigationAverage',
      header: 'Process Area Mitigation Average',
      type: 'percentage',
      width: 45,
    },
  ];

  return (
    <>
      <TitleUpdater title={`Process Area & Process Mitigation`} />
      <TitleBar
        title="Mitigation"
        subtitle="Process Area & Process Mitigation"
        brief="Select a location to add process area / process mitigation for."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Locations"
          columns={columns}
          data={filteredLocations}
          loading={loading || refreshedLoading}
          extendActions={extendActions}
          customStyles="custom-table--dependency-mapping"
          onRefresh={handleRefresh}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer goBack />
    </>
  );
}
